import { queryOptions, useQuery } from "@tanstack/react-query";
import { useRequiredParams } from "../utils/url";
import { apiRequest } from "../auth/api-request";
import {
  Device,
  DeviceLite,
  DeviceResponse,
  DeviceState,
  DeviceStateResponse,
  ExternalDevice,
} from "../models/device.model";
import {
  ApiResponse,
  ApiResponsePaginated,
  GetAllParams,
  QueryOptions,
} from "../consts";
import { useSuspenseQueryDeferred } from "../utils/query-hooks";
import { featureFlags } from "../feature-flag/feature-flag.utils";

type GetDevicesParams = {
  levelId?: number;
  deviceId?: number;
  companyId: number;
  status?: number[];
  version?: string[];
} & GetAllParams;

export const deviceQueries = {
  all: (params: GetDevicesParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["get", "devices", params],
      queryFn: () => fetchDevices(params),
      ...options,
    }),
  detail: (params: { companyId: number; id: number }, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["get", "device", params],
      queryFn: () => fetchDevice(params),
      ...options,
    }),
  checkValidity: (
    params: {
      companyId: number;
      ipAddress: string;
      port: string;
    },
    options?: QueryOptions,
  ) =>
    queryOptions({
      queryKey: ["exists", params],
      queryFn: () => checkDeviceValidity(params),
      ...options,
    }),
  status: (companyId: number, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["get", "device-status", companyId],
      queryFn: () => fetchDeviceStatuses(companyId),
      ...options,
    }),
};

// Queries

export function useDeviceStatusQuery(options?: QueryOptions) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(deviceQueries.status(companyId, options));
}

export function useDevicesQuery(
  params: Omit<GetDevicesParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(deviceQueries.all({ companyId, ...params }, options));
}

export function useDevicesSuspenseQuery(
  params: Omit<GetDevicesParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useSuspenseQueryDeferred(
    deviceQueries.all({ companyId, ...params }, options),
  );
}

export function useDeviceQuery(id: number, options?: QueryOptions) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(deviceQueries.detail({ companyId, id }, options));
}

type DeviceValidity = { ipAddress: string; port: string; id?: number };
export function useCheckDeviceValidity(
  params: DeviceValidity,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });

  return useQuery(
    deviceQueries.checkValidity(
      {
        companyId,
        ...params,
      },
      options,
    ),
  );
}

// Fetchers

async function fetchDeviceStatuses(companyId: number) {
  return apiRequest
    .get<
      ApiResponsePaginated<DeviceStateResponse[]>
    >(`companies/${companyId}/device-statuses`)
    .then((resp) => resp.data.data.map((d) => new DeviceState(d)));
}

async function fetchDevices({ companyId, ...params }: GetDevicesParams) {
  return apiRequest
    .get<
      ApiResponsePaginated<DeviceResponse[]>
    >(`companies/${companyId}/devices`, { params })
    .then((resp) => ({
      devices: resp.data.data.map((d) => new Device(d)),
      pagination: resp.data.meta.pagination,
    }));
}

async function fetchDevice(params: { companyId: number; id: number }) {
  return apiRequest
    .get<
      ApiResponse<DeviceLite | null>
    >(`companies/${params.companyId}/devices/${params.id}`)
    .then((resp) =>
      resp.data.data ? new ExternalDevice(resp.data.data) : null,
    );
}

async function checkDeviceValidity(params: {
  id?: number;
  companyId: number;
  ipAddress: string;
  port: string;
}) {
  const queryParams = {
    id: params.id,
    ipAddress: params.ipAddress,
    port: params.port,
    // REMINDER: Remove validateIp flags from the API as well when removing this
    validateIp: featureFlags.isFeatureEnabled("Device-IP-Validation")
      ? "true"
      : undefined,
  };

  return apiRequest
    .get<ApiResponse<{ isDuplicate: boolean }>>(
      `companies/${params.companyId}/devices/validate`,
      {
        params: queryParams,
      },
    )
    .then((resp) => resp.data.data.isDuplicate);
}
